import { Checkbox, Divider, Flex, Heading, Stack, Text, Tooltip } from '@chakra-ui/react';
import { Nullable } from '@pelicargo/types';
import { Show } from '@pelicargo/ui';
import { createColumnHelper } from '@tanstack/react-table';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';

import { AirlineInput } from '../components/Autocomplete/AirlineInput';
import { AirportInput } from '../components/Autocomplete/AirportInput';
import { RouteEditorPathForm } from '../components/RouteEditorPathForm';
import { RouteEditorSegments } from '../components/RouteEditorSegments';
import { SimpleForm } from '../components/SimpleForm';
import { VirtualTable } from '../components/VirtualTable';
import { trpc } from '../config/trpc';
import { API } from '../utils/typings';

type Route = API.SearchRoutes['routes'][0];
type TableColumns = Nullable<Route & { isSelected: boolean }>;

export const RouteEditor = () => {
  const searchMethods = useForm<{ providerId: string; path: string[] }>({
    defaultValues: { providerId: '', path: [] },
  });

  const providerId = searchMethods.watch('providerId');
  const path = searchMethods.watch('path');

  const {
    isLoading,
    isRefetching,
    data: rawData,
    refetch: refetchSearchRoutes,
  } = trpc.searchRoutes.useQuery({ providerId, path }, { refetchOnWindowFocus: false });

  const [rows, setRows] = useState<TableColumns[]>();

  const onRowClick = (row: TableColumns) => {
    setRows((prevRows) => {
      return prevRows.map((node) => {
        if (node.id !== row.id) return node;
        return { ...node, isSelected: !node?.isSelected };
      });
    });
  };

  const resetRows = useCallback(() => {
    setRows(rawData?.routes?.map((node) => ({ ...node, isSelected: false })));
  }, [setRows, rawData?.routes]);

  useEffect(() => {
    resetRows();
  }, [resetRows, rawData?.routes]);

  const selectAllRoutesByLength = useCallback(
    (length: number) => {
      setRows((prevRows) => {
        return prevRows.map((node) => {
          const nodeSegmentLength = node.path.split('-').length - 1;

          if (nodeSegmentLength !== length) return node;
          return { ...node, isSelected: true };
        });
      });
    },
    [setRows],
  );

  const selectedRows = useMemo(() => {
    return rows?.filter((node) => !!node.isSelected);
  }, [rows]);

  const selectedRouteIds = useMemo(() => {
    return selectedRows?.map((node) => node.id);
  }, [selectedRows]);

  const columnHelper = createColumnHelper<TableColumns>();
  const columns = useMemo(
    () => [
      columnHelper.accessor('isSelected', {
        header: () => {
          const hasRoutes = !!rows?.length;

          const noneSelected = (hasRoutes && selectedRows.length === 0) || !hasRoutes;
          const lengthOPathFromSelected = selectedRows?.[0]?.path?.split('-')?.length - 1;
          const allRoutesByLengthSelected = !noneSelected
            ? rows?.filter((route) => route.path.split('-').length - 1 === lengthOPathFromSelected)
            : [];
          const tooltipLabel = noneSelected
            ? 'Select at least one route'
            : `Select all ${lengthOPathFromSelected} segment routes`;
          const isAllSelected =
            hasRoutes && !noneSelected && allRoutesByLengthSelected?.length === selectedRows?.length;
          const handleChange = () => {
            if (isAllSelected) return resetRows();
            selectAllRoutesByLength(lengthOPathFromSelected);
          };

          return (
            <Tooltip label={tooltipLabel} aria-label={tooltipLabel}>
              <Checkbox isChecked={isAllSelected} onChange={handleChange} isDisabled={!selectedRows?.length} />
            </Tooltip>
          );
        },
        cell: (cell) => {
          const row = cell.row.original;
          return <Checkbox isChecked={row.isSelected} />;
        },
        size: 0,
        minSize: 0,
      }),
      columnHelper.accessor('path', {
        header: () => {
          const selectedCount = selectedRows?.length || 0;
          const hasSelectedRows = selectedCount > 0;
          const isPlural = rows?.length !== 1;

          if (hasSelectedRows) {
            return (
              <Text fontSize="sm" fontWeight="bold">
                {selectedCount} of {rows?.length} Route{isPlural ? 's' : ''} selected
              </Text>
            );
          }

          return (
            <Text fontSize="sm" fontWeight="bold">
              {rows?.length} Route{isPlural ? 's' : ''}
            </Text>
          );
        },
        size: 0,
        minSize: 80,
        cell: (cell) => <Text fontFamily="Fira Code">{cell.row.original.path}</Text>,
      }),
    ],
    [columnHelper, rows, selectedRows, resetRows, selectAllRoutesByLength],
  );

  return (
    <Stack spacing="0" maxH="100vh" h="full" divider={<Divider />}>
      <Stack direction="row" alignItems="center" zIndex="2" px={6} py={4} spacing={6} minH="72px">
        <Heading variant="h1">Route Editor</Heading>
        <SimpleForm {...(searchMethods as any)}>
          <Stack direction="row" spacing="1">
            <AirlineInput displayValue="id" name="providerId" style={{ width: '48px' }} tabIndex={0} />
            <Flex minW="96">
              <AirportInput
                name="path"
                placeholder="ATL -> ALL -> FRA"
                tabIndex={0}
                extraOptions={[{ label: 'Any', value: 'Any' }]}
                isMulti
              />
            </Flex>
          </Stack>
        </SimpleForm>
      </Stack>

      <Flex direction="row" w="full" h="full" overflowX="hidden">
        <Flex h="full" overflowY="auto" p={0} borderRightWidth={1}>
          <VirtualTable
            isLoading={isLoading || isRefetching}
            data={rows ?? []}
            columns={columns}
            onRowClick={onRowClick}
          />
        </Flex>
        <Stack w="full" h="full" overflowY="auto">
          <Show if={rows?.length > 0}>
            <Flex w="full" p={6} borderBottomWidth={1}>
              <RouteEditorPathForm routes={selectedRows} refetch={refetchSearchRoutes} />
            </Flex>
            <Show if={!!providerId && !!selectedRouteIds?.length}>
              <RouteEditorSegments providerId={providerId} routeIds={selectedRouteIds} />
            </Show>
          </Show>
        </Stack>
      </Flex>
    </Stack>
  );
};
